/**
 * WelcomeRoomPage — the emotional finish to onboarding.
 *
 * A new customer lands here after they complete the four-step setup.
 * Not the Today room — a quiet welcome, an acknowledgement of what
 * they've already created, and a promise from ARA about tomorrow.
 *
 * Once the resident enters the Residence, `markFirstFiveCelebrated()`
 * flags the milestone so this page never returns automatically.
 */
import { useMemo } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Check } from "lucide-react";
import BetaFeedbackButton from "../../components/BetaFeedbackButton";
import { useHomeOSState } from "./state/HomeOSStateProvider";
import {
  ResidenceLayout, PageHeading, EditorialSection,
  TextLink, PillButton, Colophon, useLightPhase,
} from "./components/os/editorial";
import { ResidenceRail } from "./components/os/ResidenceRail";

const MILESTONES = [
  { id: "familyMember", label: "Family added",           empty: "Add your first family member",  route: "/home-os/family" },
  { id: "meal",         label: "First recipe added",     empty: "Add a recipe to the book",       route: "/home-os/meals" },
  { id: "pantryItem",   label: "Pantry started",         empty: "Add the first pantry item",      route: "/home-os/supplies" },
  { id: "document",     label: "First document stored",  empty: "Store a document",                route: "/home-os/documents" },
  { id: "bill",         label: "First bill added",       empty: "Add a bill or payment",           route: "/home-os/money" },
];

export default function WelcomeRoomPage() {
  const { phase } = useLightPhase();
  const navigate = useNavigate();
  const state = useHomeOSState();
  const { household, firstFive, markFirstFiveCelebrated } = state;

  const doneCount = MILESTONES.filter(m => firstFive?.[m.id]).length;
  const householdName = (household?.name || "your family").trim();

  const focal = useMemo(() => {
    if (doneCount === 0) return `Welcome home, ${householdName.split(' ')[0] || 'friend'}.`;
    if (doneCount === MILESTONES.length) return `Everything is in place — your Residence is ready.`;
    return `A calm beginning, ${householdName.split(' ')[0] || 'friend'}.`;
  }, [doneCount, householdName]);

  /* Guard: if the household hasn't finished setup, send them there.
     If they've already been celebrated, send them straight to Today.
     Wait for backend hydration first so a fresh device sees its real residence. */
  if (!state.hydrated) return null;
  if (!household?.onboarded) return <Navigate to="/home-os/setup" replace />;
  if (firstFive?.celebrated) return <Navigate to="/home-os" replace />;

  const enterResidence = () => {
    if (markFirstFiveCelebrated) markFirstFiveCelebrated();
    navigate("/home-os");
  };

  return (
    <ResidenceLayout rail={<ResidenceRail currentKey="today" />} phase={phase} testid="module-page-welcome">
      <PageHeading
        testid="welcome-hero"
        module="today"
        room="Welcome"
        title={household?.name ? `Welcome, ${household.name}` : "Welcome home"}
        focal={focal}
        actions={
          <>
            <PillButton variant="solid" onClick={enterResidence} testid="welcome-enter-btn">
              Enter your Residence →
            </PillButton>
            <TextLink onClick={() => navigate("/home-os/setup")} testid="welcome-back-to-setup">
              Back to setup
            </TextLink>
          </>
        }
      />

      <EditorialSection
        testid="welcome-milestones"
        moduleMark={{ module: 'today', label: 'Your Residence' }}
        title="Here's what you've created"
      >
        <ul>
          {MILESTONES.map((m, i, arr) => {
            const done = !!firstFive?.[m.id];
            return (
              <li
                key={m.id}
                className={`grid grid-cols-[28px_1fr_auto] items-baseline gap-4 py-4 ${i === 0 ? 'border-t' : ''} ${i === arr.length - 1 ? '' : 'border-b'}`}
                style={{ borderColor: 'var(--ara-line-soft)' }}
                data-testid={`welcome-milestone-${m.id}`}
              >
                <span
                  className="w-6 h-6 rounded-full flex items-center justify-center"
                  style={{
                    background: done ? 'var(--ara-mod-meals)' : 'transparent',
                    border: done ? '1px solid var(--ara-mod-meals)' : '1px solid var(--ara-line)',
                  }}
                  aria-hidden
                >
                  {done && <Check className="w-3.5 h-3.5" strokeWidth={1.5} style={{ color: 'var(--ara-surface)' }} />}
                </span>
                <p
                  className="text-[15px] leading-tight"
                  style={{
                    fontFamily: 'Inter, system-ui, sans-serif',
                    color: done ? 'var(--ara-ink)' : 'var(--ara-ink-muted)',
                    fontWeight: done ? 500 : 400,
                  }}
                >
                  {done ? m.label : m.empty}
                </p>
                {!done && (
                  <TextLink onClick={() => navigate(m.route)} testid={`welcome-add-${m.id}`}>
                    Add now
                  </TextLink>
                )}
              </li>
            );
          })}
        </ul>
      </EditorialSection>

      <EditorialSection
        testid="welcome-ara-promise"
        moduleMark={{ module: 'today', label: 'Ara' }}
        title="A promise from Ara"
      >
        <p
          className="italic text-[19px] sm:text-[22px] leading-[1.4]"
          style={{
            fontFamily: "var(--ara-font-display), 'Times New Roman', serif",
            color: 'var(--ara-ink)', fontWeight: 400, maxWidth: '38ch',
          }}
          data-testid="welcome-ara-sentence"
        >
          Your Residence is ready. From tomorrow morning, I'll begin helping you stay one step ahead.
        </p>
      </EditorialSection>

      <Colophon>ARA · Welcome</Colophon>
      <BetaFeedbackButton />
    </ResidenceLayout>
  );
}
